import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup } from '@vibesites/runtime/react';
import { EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

const RUNTIME_DEF: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'property_featured_image' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Gallery banner' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt: 'A strong, representative property photo — preferably a building exterior or lifestyle — that invites browsing the photo gallery. Never use monument signs, entry signs, logos, brand marks, floor plan drawings, line art, or unit layout graphics.',
    context: 'Full-bleed banner hero on the Property Photos / Gallery page. Prefer a standout building exterior or lifestyle shot — not a monument or entry sign. Must be a different photo from every other page hero. Do not reuse other page hero images. Never use logos or floor plan layout images.',
  },
};

export function GalleryHero() {
  const bgImage = useSlotGroup('galleryhero.bg_image', RUNTIME_DEF);

  return (
    <section className="relative h-[42vh] min-h-[300px] max-h-[420px] overflow-hidden">
      <EditableSlotGroup groupPath="galleryhero.bg_image" definition={RUNTIME_DEF} className="absolute inset-0">
        {bgImage.image ? (
          <VibesitesImage url={bgImage.image} alt={bgImage.alt || 'Gallery banner'} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-[var(--vibe-color-text)]" />
        )}
        <div className="absolute inset-0 bg-black/30" />
      </EditableSlotGroup>

      <div className="relative z-10 flex flex-col items-center justify-center h-full text-center text-white px-4" style={{ paddingTop: 'calc(var(--offer-banner-height, 0px) + 72px)' }}>
        <RichTextContent path="galleryhero.title" as="h1" className="font-heading text-3xl sm:text-4xl md:text-5xl font-normal mb-3 uppercase tracking-wider" />
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'GalleryHero',
  displayName: 'Gallery Hero',
  tokens: {
    'galleryhero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Gallery</p>',
    },
  },
  slotGroups: {
    'galleryhero.bg_image': RUNTIME_DEF,
  },
};

(GalleryHero as any).contentSchema = contentSchema;

export default GalleryHero;
